import React, { useState } from "react"
import { useNavigate } from "@tanstack/react-router"
import { useQueryState, parseAsStringLiteral } from "nuqs"
import toast from "react-hot-toast"
import { Star, Plus, Archive, ArchiveRestore, Trash2, MessageSquare } from "lucide-react"
import { Button, Input } from "@aix/ui"
import { useLocalStorage } from "@aix/ui"
import { trpc } from "../../lib/trpc"
import { DebugTab } from "./DebugTab"

type TabType = "all" | "starred" | "archived" | "debug"

// Project type colors
const PROJECT_TYPE_COLORS: Record<string, string> = {
  "web-chat": "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  telegram: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
  experimental: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200",
  "todoist-integration": "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
}

const AIProjectsScreen: React.FC = () => {
  const navigate = useNavigate()

  // URL state via nuqs
  const [activeTab, setActiveTab] = useQueryState(
    "tab",
    parseAsStringLiteral(["all", "starred", "archived", "debug"]).withDefault("all"),
  )

  // State management
  const [searchQuery, setSearchQuery] = useState("")
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [newProjectName, setNewProjectName] = useState("")
  const [newProjectDescription, setNewProjectDescription] = useState("")
  const [newProjectType, setNewProjectType] = useState<string>("web-chat")

  // Starred projects stored in localStorage (like nav sidebar)
  const [starredProjectIds, setStarredProjectIds] = useLocalStorage<string[]>("aix-starred-ai-projects", [])

  // API queries
  const {
    data: projects,
    isLoading,
    refetch,
  } = trpc.aiChat.listProjects.useQuery({
    include_archived: activeTab === "archived",
  })

  const utils = trpc.useUtils()

  // Mutations
  const createProjectMutation = trpc.aiChat.createProject.useMutation({
    onSuccess: () => {
      toast.success("Project created successfully")
      setShowCreateModal(false)
      setNewProjectName("")
      setNewProjectDescription("")
      setNewProjectType("web-chat")
      void utils.aiChat.listProjects.invalidate()
    },
    onError: (error) => {
      toast.error(error.message || "Failed to create project")
    },
  })

  const deleteProjectMutation = trpc.aiChat.deleteProject.useMutation({
    onSuccess: () => {
      toast.success("Project deleted")
      void utils.aiChat.listProjects.invalidate()
    },
    onError: (error) => {
      toast.error(error.message || "Failed to delete project")
    },
  })

  // Handlers
  const handleTabChange = (tab: TabType) => {
    void setActiveTab(tab, { history: "push" })
  }

  const handleToggleStar = (projectId: string) => {
    const isCurrentlyStarred = starredProjectIds.includes(projectId)
    if (isCurrentlyStarred) {
      setStarredProjectIds(starredProjectIds.filter((id) => id !== projectId))
      toast.success("Removed from starred")
    } else {
      setStarredProjectIds([...starredProjectIds, projectId])
      toast.success("Added to starred")
    }
  }

  const handleDelete = (projectId: string) => {
    if (window.confirm("Are you sure you want to delete this project? This will also delete all conversations.")) {
      // Also remove from starred if it was starred
      if (starredProjectIds.includes(projectId)) {
        setStarredProjectIds(starredProjectIds.filter((id) => id !== projectId))
      }
      deleteProjectMutation.mutate({ id: projectId })
    }
  }

  const handleCreateProject = () => {
    if (!newProjectName.trim()) {
      toast.error("Project name is required")
      return
    }
    createProjectMutation.mutate({
      name: newProjectName.trim(),
      description: newProjectDescription.trim() || undefined,
      project_type: newProjectType as "web-chat" | "telegram" | "experimental" | "todoist-integration",
    })
  }

  const handleOpenProject = (projectId: string) => {
    navigate({
      to: "/ai-chat",
      // @ts-ignore
      search: { projectId },
    })
  }

  // Filter and sort projects
  const getFilteredProjects = () => {
    if (!projects) return []

    let filtered = projects

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(
        (project) =>
          project.name?.toLowerCase().includes(query) ||
          project.description?.toLowerCase().includes(query) ||
          project.project_type?.toLowerCase().includes(query),
      )
    }

    // Filter by starred tab
    if (activeTab === "starred") {
      filtered = filtered.filter((project) => starredProjectIds.includes(project.id))
    }

    // Sort: starred first, then by last_active_at
    return filtered.sort((a, b) => {
      const aStarred = starredProjectIds.includes(a.id)
      const bStarred = starredProjectIds.includes(b.id)
      if (aStarred && !bStarred) return -1
      if (!aStarred && bStarred) return 1
      return new Date(b.last_active_at).getTime() - new Date(a.last_active_at).getTime()
    })
  }

  const filteredProjects = getFilteredProjects()

  return (
    <div className="min-h-screen bg-background">
      <div className="px-6 py-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">AI Projects</h1>
          <p className="text-muted-foreground">Manage your AI conversation projects and workspaces.</p>
        </div>

        <div className="flex gap-6">
          {/* Main Content */}
          <div className="flex-1">
            {/* Actions Bar */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex-1 max-w-md">
                <Input
                  type="text"
                  placeholder="Search projects..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full"
                />
              </div>
              <Button onClick={() => setShowCreateModal(true)} className="ml-4">
                <Plus className="w-4 h-4 mr-2" />
                New Project
              </Button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-border mb-6">
              <button
                onClick={() => handleTabChange("all")}
                className={`px-4 py-2 font-medium transition-colors ${
                  activeTab === "all"
                    ? "border-b-2 border-primary text-primary"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                All Projects
              </button>
              <button
                onClick={() => handleTabChange("starred")}
                className={`px-4 py-2 font-medium transition-colors flex items-center gap-2 ${
                  activeTab === "starred"
                    ? "border-b-2 border-primary text-primary"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Star className="w-4 h-4" />
                Starred ({starredProjectIds.length})
              </button>
              <button
                onClick={() => handleTabChange("archived")}
                className={`px-4 py-2 font-medium transition-colors flex items-center gap-2 ${
                  activeTab === "archived"
                    ? "border-b-2 border-primary text-primary"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Archive className="w-4 h-4" />
                Archived
              </button>
              <button
                onClick={() => handleTabChange("debug")}
                className={`px-4 py-2 font-medium transition-colors ${
                  activeTab === "debug"
                    ? "border-b-2 border-primary text-primary"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Debug
              </button>
            </div>

            {/* Content */}
            {activeTab === "debug" ? (
              <DebugTab starredProjectIds={starredProjectIds} />
            ) : isLoading ? (
              <div className="bg-background border-2 border-border rounded p-8 text-center text-muted-foreground">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-2"></div>
                <div>Loading projects...</div>
              </div>
            ) : filteredProjects && filteredProjects.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredProjects.map((project) => {
                  const isStarred = starredProjectIds.includes(project.id)
                  return (
                    <div
                      key={project.id}
                      className="bg-card border border-border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer group"
                      onClick={() => handleOpenProject(project.id)}
                    >
                      {/* Header */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            {isStarred && <Star className="w-4 h-4 fill-yellow-400 text-yellow-400 flex-shrink-0" />}
                            <h3 className="font-semibold text-foreground truncate">
                              {project.name || "Untitled Project"}
                            </h3>
                          </div>
                          {project.description && (
                            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{project.description}</p>
                          )}
                        </div>
                        {project.color && (
                          <div
                            className="w-3 h-3 rounded-full flex-shrink-0 ml-2"
                            style={{ backgroundColor: project.color }}
                          />
                        )}
                      </div>

                      {/* Type Badge */}
                      <div className="mb-3">
                        <span
                          className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                            PROJECT_TYPE_COLORS[project.project_type || "web-chat"] ||
                            "bg-secondary text-secondary-foreground"
                          }`}
                        >
                          {project.project_type || "web-chat"}
                        </span>
                      </div>

                      {/* Meta Info */}
                      <div className="text-xs text-muted-foreground mb-3">
                        <div>Created: {new Date(project.created_at).toLocaleDateString()}</div>
                        <div>Last active: {new Date(project.last_active_at).toLocaleDateString()}</div>
                      </div>

                      {/* Actions */}
                      <div
                        className="flex items-center gap-2 pt-3 border-t border-border opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          onClick={() => handleToggleStar(project.id)}
                          className="p-1.5 rounded hover:bg-accent transition-colors"
                          title={isStarred ? "Unstar" : "Star"}
                        >
                          <Star
                            className={`w-4 h-4 ${
                              isStarred ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground"
                            }`}
                          />
                        </button>
                        <button
                          onClick={() => handleOpenProject(project.id)}
                          className="p-1.5 rounded hover:bg-accent transition-colors"
                          title="Open conversations"
                        >
                          <MessageSquare className="w-4 h-4 text-muted-foreground" />
                        </button>
                        <button
                          onClick={() => handleDelete(project.id)}
                          className="p-1.5 rounded hover:bg-destructive/10 transition-colors ml-auto"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">📁</div>
                <h3 className="text-lg font-medium text-foreground mb-2">
                  {activeTab === "starred"
                    ? "No starred projects"
                    : activeTab === "archived"
                      ? "No archived projects"
                      : "No projects yet"}
                </h3>
                <p className="text-muted-foreground mb-4">
                  {activeTab === "starred"
                    ? "Star projects to see them here"
                    : activeTab === "archived"
                      ? "Archived projects will appear here"
                      : "Create your first AI project to get started"}
                </p>
                {activeTab === "all" && (
                  <Button onClick={() => setShowCreateModal(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Project
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Project Modal */}
      {showCreateModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setShowCreateModal(false)}
        >
          <div
            className="bg-card border border-border rounded-lg p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold text-foreground mb-4">Create New Project</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Project Name *</label>
                <Input
                  type="text"
                  placeholder="My AI Project"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Description</label>
                <textarea
                  placeholder="Optional description..."
                  value={newProjectDescription}
                  onChange={(e) => setNewProjectDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground placeholder-muted-foreground focus:outline-hidden focus:ring-2 focus:ring-primary resize-none"
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Project Type</label>
                <select
                  value={newProjectType}
                  onChange={(e) => setNewProjectType(e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-hidden focus:ring-2 focus:ring-primary"
                >
                  <option value="web-chat">Web Chat</option>
                  <option value="telegram">Telegram</option>
                  <option value="experimental">Experimental</option>
                  <option value="todoist-integration">Todoist Integration</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateProject} loading={createProjectMutation.isLoading}>
                Create Project
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AIProjectsScreen
